import { buildApiPayload } from '../../shared/utils/buildApiPayload';
import { convertParams } from './convertParams';
import { AuthenticateError, DuplicateAccountError } from '../../shared/types/authErrors';
import { createAccount, getAuthorizeUrl, getAuthorizeForReconnectUrl, reconnectAccount, migrationReconnectAccount } from '../api/UnifiedAccountApi';
export function resolveOAuthScopeSet(oAuthScope, oAuthScopes) {
  if (oAuthScopes !== undefined) {
    return oAuthScopes;
  }
  if (!oAuthScope) {
    return undefined;
  }
  const scopes = oAuthScope.split(/[\s,]+/).filter(Boolean);
  return scopes.length > 0 ? scopes : undefined;
}
export function prepareOAuthParams(oAuthParams) {
  return Object.assign({
    state: oAuthParams.state
  }, oAuthParams.scopeSet && {
    scopeSet: oAuthParams.scopeSet
  }, oAuthParams.mcpServerUrl && {
    mcpServerUrl: oAuthParams.mcpServerUrl
  }, oAuthParams.clientId && {
    clientId: oAuthParams.clientId
  });
}
export function prepareAccountRequest(payload, appId) {
  return Object.assign({
    appId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams)
  }, payload.oAuthParams && {
    oauthParams: prepareOAuthParams(payload.oAuthParams)
  });
}
export function prepareReconnectAccountRequest(payload, accountId, incremental) {
  return Object.assign({
    accountId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams)
  }, payload.oAuthParams && {
    oauthParams: prepareOAuthParams(payload.oAuthParams)
  }, incremental !== undefined && {
    incremental
  });
}
export function createBuildAuthPayload(appId) {
  return function buildAuthPayloadFn(params) {
    return Promise.resolve(buildApiPayload(params.sessionId, params.steps, params.urlParams, params.oAuthParams, params.oAuthState, params.oAuthScopeSet, params.mcpParams, appId));
  };
}
export function createAuthorizeUrlFetcher({
  portalId,
  appId,
  sessionId
}) {
  return async function makeAuthorizeUrlDescriptor(payload) {
    const response = await getAuthorizeUrl(portalId, Object.assign({
      appId,
      sessionId,
      inputParams: convertParams(payload.inputParams)
    }, payload.oAuthParams && {
      oauthParams: prepareOAuthParams(payload.oAuthParams)
    }));
    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(response.localizedErrorMessage, response.extraDetailsMessage);
    }
    return {
      authorizeUrl: response.authorizeUrl
    };
  };
}
export function createReconnectAuthorizeUrlFetcher({
  portalId,
  accountId,
  sessionId,
  incremental
}) {
  return async function makeAuthorizeUrlDescriptor(payload) {
    const response = await getAuthorizeForReconnectUrl(portalId, Object.assign({
      accountId,
      sessionId,
      inputParams: convertParams(payload.inputParams)
    }, payload.oAuthParams && {
      oauthParams: prepareOAuthParams(payload.oAuthParams)
    }, incremental !== undefined && {
      incremental
    }));
    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(response.localizedErrorMessage, response.extraDetailsMessage);
    }
    return {
      authorizeUrl: response.authorizeUrl
    };
  };
}
export function createFinishNewAccountHandler({
  portalId,
  appId
}) {
  return async function finishAuthFlow(payload) {
    const request = prepareAccountRequest(payload, appId);
    const response = await createAccount(portalId, request);
    if (response['@result'] !== 'OK') {
      if (response.errorType === 'ACCOUNT_ALREADY_EXISTS' && response.existingAccountId !== undefined) {
        throw new DuplicateAccountError(response.existingAccountId);
      }
      throw new AuthenticateError(response.localizedErrorMessage, response.extraDetailsMessage);
    }
    return {
      accountId: response.accountId
    };
  };
}
export function createFinishReconnectHandler({
  portalId,
  accountId,
  incremental
}) {
  return async function finishAuthFlow(payload) {
    const request = prepareReconnectAccountRequest(payload, accountId, incremental);
    const response = await reconnectAccount(portalId, request);
    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(response.localizedErrorMessage, response.extraDetailsMessage);
    }
    return {
      accountId
    };
  };
}
export function prepareMigrationReconnectRequest(payload, appId, oAuthorAccountId) {
  return Object.assign({
    appId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams)
  }, payload.oAuthParams && {
    oauthParams: prepareOAuthParams(payload.oAuthParams)
  }, oAuthorAccountId && {
    oAuthorAccountId
  });
}
export function createFinishMigrationReconnectHandler({
  portalId,
  appId,
  oAuthorAccountId
}) {
  return async function finishAuthFlow(payload) {
    const request = prepareMigrationReconnectRequest(payload, appId, oAuthorAccountId);
    const response = await migrationReconnectAccount(portalId, request);
    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(response.localizedErrorMessage, response.extraDetailsMessage);
    }
    return {
      accountId: response.accountId
    };
  };
}